import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkle, CreditCard, CheckCircle, Stack } from '@phosphor-icons/react';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import {PaystackButton, usePaystackPayment} from 'react-paystack';
import { GoogleGenAI } from '@google/genai';
import {useFirebaseAuth} from "../context/FirebaseAuthContext";
import {db} from "../lib/firebase";
import {Button} from "../components/ui/button";
import {Card} from "../components/ui/card";
import SEO from "../seo/SEO";

// ECSA Outcomes Definition
const ECSA_OUTCOMES = [
    {
        number: 1,
        title: "Problem Solving",
        description: "Identify, formulate, analyze, and solve complex engineering problems creatively and innovatively."
    },
    {
        number: 2,
        title: "Application of scientific knowledge",
        description: "Apply knowledge of mathematics, natural science, engineering fundamentals and an engineering specialization to solve complex engineering problems."
    },
    {
        number: 3,
        title: "Engineering design",
        description: "Perform creative, procedural and non-procedural design of components, systems, works and products to meet user and societal needs."
    },
    {
        number: 4,
        title: "Investigations and experiments",
        description: "Conduct investigations of complex problems using research-based knowledge and research methods including design of experiments."
    },
    {
        number: 5,
        title: "Engineering methods and tools",
        description: "Select and apply appropriate engineering methods, skills, tools and techniques including synthesis and modelling, to solve complex engineering problems."
    },
    {
        number: 6,
        title: "Professional communication",
        description: "Communicate effectively on complex engineering activities with the engineering community and with society at large."
    },
    {
        number: 7,
        title: "Sustainability and impact",
        description: "Evaluate and address the impact of engineering activities on the environment and sustainable development."
    },
    {
        number: 8,
        title: "Engineering professionalism",
        description: "Demonstrate knowledge and understanding of engineering management principles and economic decision-making."
    },
    {
        number: 9,
        title: "Independent learning",
        description: "Demonstrate independent learning and the ability to engage in lifelong learning."
    },
    {
        number: 10,
        title: "Engineering judgment",
        description: "Demonstrate engineering judgment and decision-making in complex and uncertain situations."
    },
    {
        number: 11,
        title: "Project management",
        description: "Plan, organize and manage engineering projects and teams effectively."
    }
];

const ConsolidatedAssessmentPage = () => {
    const { consolidationId } = useParams();
    const navigate = useNavigate();
    const { user, userData } = useFirebaseAuth();
    const [workExperiences, setWorkExperiences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paying, setPaying] = useState(false);
    const [hasPaid, setHasPaid] = useState(false);
    const [pricing, setPricing] = useState({ consolidated_price: 250, currency: 'ZAR' });
    const [checkingPayment, setCheckingPayment] = useState(true);
    const [paymentReference, setPaymentReference] = useState(null);

    // Initialize Google GenAI
    const ai = new GoogleGenAI({
        apiKey: process.env.REACT_APP_GOOGLE_AI_API_KEY || "AQ.Ab8RN6J2R0YqIa0vOix-j4rtDC6isXFHmcAUl4mEGQNpX43Yyg"
    });

    const config = {
        reference: (new Date()).getTime().toString(),
        email: user?.email || '',
        amount: pricing.consolidated_price * 100, // Amount in cents/kobo
        publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_d1b62c4a430407c1a991be4d6c4b258b83b896b6',
        currency: pricing.currency,
    };

    useEffect(() => {
        if (user) {
            fetchInitialData();
        } else {
            navigate('/');
            toast.error('Please login to access this page');
        }
    }, [consolidationId, user]);

    const handlePaystackSuccessAction = (reference) => {
        // Implementation for whatever you want to do with reference and after success call.
        handlePayment(reference);
    };

    // you can call this function anything
    const handlePaystackCloseAction = () => {
        // implementation for  whatever you want to do when the Paystack dialog closed.
        console.log('closed')
    }

    const componentProps = {
        ...config,
        text: `Pay R${pricing.consolidated_price} with Paystack`,
        onSuccess: (reference) => handlePaystackSuccessAction(reference),
        onClose: handlePaystackCloseAction,
    };

    const fetchInitialData = async () => {
        if (!user) return;

        try {
            // Fetch all work experiences for this user
            const workExpRef = collection(db, 'workExperiences');
            const workExpQuery = query(
                workExpRef,
                where('userId', '==', user.uid)
            );
            const workExpSnapshot = await getDocs(workExpQuery);
            const workExpList = workExpSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                created_at: doc.data().created_at?.toDate?.() || new Date(doc.data().created_at)
            }));
            setWorkExperiences(workExpList);

            // Check if payment has been made for this consolidated assessment
            await checkPaymentStatus(consolidationId);

            // Fetch pricing from Firestore config
            // const configRef = doc(db, 'config', 'pricing');
            // const configDoc = await getDoc(configRef);
            // if (configDoc.exists()) {
            //     const configData = configDoc.data();
            //     if (configData.consolidated_price) {
            //         setPricing(configData);
            //     }
            // }

        } catch (error) {
            console.error('Error fetching consolidation data:', error);
            toast.error('Failed to load consolidation data');
            navigate('/dashboard');
        } finally {
            setCheckingPayment(false);
        }
    };

    const checkPaymentStatus = async (consolidationId) => {
        try {
            const paymentsRef = collection(db, 'payments');
            const paymentsQuery = query(
                paymentsRef,
                where('consolidationId', '==', consolidationId),
                where('userId', '==', user.uid),
                where('purpose', '==', 'consolidated'),
                where('status', '==', 'completed')
            );
            const paymentsSnapshot = await getDocs(paymentsQuery);

            if (!paymentsSnapshot.empty) {
                setHasPaid(true);
                const payment = paymentsSnapshot.docs[0];
                setPaymentReference(payment.id);
            }
        } catch (error) {
            console.error('Error checking payment status:', error);
        }
    };

    const handlePayment = async (reference) => {
        setPaying(true);
        try {
            const paymentData = {
                userId: user.uid,
                consolidationId: consolidationId,
                amount: pricing.consolidated_price,
                currency: pricing.currency,
                purpose: 'consolidated',
                status: 'completed',
                created_at: new Date().toISOString(),
                user_email: user.email,
                user_name: userData?.full_name || user.displayName,
                paymentReference: reference
            };

            await addDoc(collection(db, 'payments'), paymentData);
            setHasPaid(true);
            toast.success('Payment successful! You can now run the consolidated assessment.');
            setPaying(false);

        } catch (error) {
            console.error('Payment initialization error:', error);
            toast.error('Failed to process payment. Please try again.');
            setPaying(false);
        }
    };

    const performConsolidatedAssessment = async (workExperiences, userData) => {
        try {
            // Prepare assessment prompt for consolidated analysis
            const outcomesText = ECSA_OUTCOMES.map((o, i) =>
                `${i + 1}. ${o.title} - ${o.description}`
            ).join('\n');

            // Format work experiences for the prompt
            const experiencesText = workExperiences.map((we, idx) => {
                return `
Work Experience ${idx + 1}:
Title: ${we.title}
Description: ${we.description}
${we.original_filename ? 'Attached Document: Yes' : 'Attached Document: No'}
`;
            }).join('\n---\n');

            const prompt = `
You are an expert ECSA (Engineering Council of South Africa) assessor evaluating a candidate engineer's readiness for professional engineer registration through a CONSOLIDATED assessment of ALL their work experiences.

Candidate Information:
- Name: ${userData?.full_name || user?.displayName || 'Not specified'}
- Discipline: ${userData?.discipline || 'Not specified'}
- Years of Experience: ${userData?.years_of_experience || 'Not specified'}
- Total Work Experiences Submitted: ${workExperiences.length}

All Work Experiences Submitted:
${experiencesText}

Your task is to provide a HOLISTIC assessment of this candidate's readiness against the 11 ECSA outcomes for Professional Engineer registration, considering ALL their work experiences together.

ECSA Outcomes:
${outcomesText}

IMPORTANT: This is a CONSOLIDATED assessment. You MUST:
1. Look at the candidate's ENTIRE career profile, not just individual experiences
2. Identify patterns and themes across all experiences
3. Determine the overall readiness level based on the COMBINED evidence
4. Identify which outcomes are well-supported and which need more evidence
5. Provide specific recommendations for filling gaps

Please provide:
1. A score (0-100) for each of the 11 outcomes based on the consolidated evidence across ALL experiences
2. An overall consolidated assessment score (0-100)
3. A consolidated readiness level: "Not Ready" (<50), "Developing" (50-69), "Ready" (70-84), "Highly Ready" (85+)
4. 3-5 key strengths demonstrated across all experiences
5. 3-5 areas for improvement or gaps needing evidence
6. Detailed recommendations for achieving professional engineer status

Provide your response in the following JSON format:
{
  "outcome_scores": [
    {"outcome_number": 1, "score": 75, "evidence": "Brief explanation based on consolidated evidence"},
    ...
  ],
  "overall_score": 72,
  "readiness_level": "Ready",
  "strengths": ["Strength 1", "Strength 2", ...],
  "areas_for_improvement": ["Area 1", "Area 2", ...],
  "recommendations": "Detailed recommendations text",
  "gap_analysis": "Summary of which outcomes need more evidence",
  "consolidated_feedback": "Overall feedback on the candidate's career progression"
}
`;

            // Use Google Gemini
            const modelName = 'gemini-3.5-flash';

            // Prepare content for Gemini
            const contents = [{ text: prompt }];

            // Get consolidated assessment from Gemini
            const geminiResponse = await ai.models.generateContent({
                model: "gemini-3.5-flash",
                contents: contents,
                config: {
                    temperature: 0.7,
                    topP: 0.95,
                    topK: 40,
                    maxOutputTokens: 8192,
                }
            });

            // Parse the response
            let responseText = geminiResponse.text || '';

            // Extract JSON from response
            if (responseText.includes('```json')) {
                responseText = responseText.split('```json')[1].split('```')[0];
            } else if (responseText.includes('```')) {
                responseText = responseText.split('```')[1].split('```')[0];
            }

            responseText = responseText.trim();

            // Parse JSON
            let result;
            try {
                result = JSON.parse(responseText);
            } catch (parseError) {
                console.error('JSON Parse Error:', parseError);
                const jsonMatch = responseText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    try {
                        result = JSON.parse(jsonMatch[0]);
                    } catch (e) {
                        throw new Error('Failed to parse assessment response');
                    }
                } else {
                    throw new Error('Invalid response format');
                }
            }

            // Validate the result structure
            if (!result.outcome_scores || !result.overall_score || !result.readiness_level) {
                throw new Error('Incomplete assessment data');
            }

            // Create consolidated assessment result object
            const assessment = {
                userId: user.uid,
                consolidationId: consolidationId,
                isConsolidated: true,
                workExperienceIds: workExperiences.map(we => we.id),
                workExperienceCount: workExperiences.length,
                modelUsed: modelName,
                overallScore: result.overall_score,
                readinessLevel: result.readiness_level,
                outcomeScores: result.outcome_scores,
                strengths: result.strengths || [],
                areasForImprovement: result.areas_for_improvement || [],
                recommendations: result.recommendations || '',
                gapAnalysis: result.gap_analysis || '',
                consolidatedFeedback: result.consolidated_feedback || '',
                createdAt: new Date().toISOString()
            };

            return assessment;

        } catch (error) {
            console.error('Consolidated assessment error:', error);
            throw new Error(`Consolidated assessment failed: ${error.message}`);
        }
    };

    const handleAssess = async () => {
        if (workExperiences.length < 2) {
            toast.error('You need at least 2 work experiences for a consolidated assessment');
            return;
        }

        setLoading(true);
        try {
            // Perform consolidated AI assessment
            const assessment = await performConsolidatedAssessment(workExperiences, userData);

            // Store consolidated assessment result in Firestore
            const assessmentData = {
                userId: user.uid,
                consolidationId: consolidationId,
                workExpIds: workExperiences.map(we => we.id),
                is_consolidated: true,
                work_exp_count: workExperiences.length,
                workExpTitle: 'Consolidated Assessment of ' + workExperiences.length + ' experiences',
                overall_score: assessment.overallScore,
                readiness_level: assessment.readinessLevel,
                model_used: assessment.modelUsed,
                outcome_scores: assessment.outcomeScores,
                strengths: assessment.strengths,
                areas_for_improvement: assessment.areasForImprovement,
                recommendations: assessment.recommendations,
                gap_analysis: assessment.gapAnalysis,
                consolidated_feedback: assessment.consolidatedFeedback,
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
                download_count: 0,
                view_count: 0
            };

            const assessmentRef = await addDoc(collection(db, 'assessments'), assessmentData);

            // Update each work experience with the consolidated assessment reference
            for (const we of workExperiences) {
                const workExpRef = doc(db, 'workExperiences', we.id);
                await updateDoc(workExpRef, {
                    consolidated_assessment_id: assessmentRef.id,
                    consolidated_assessed_at: serverTimestamp()
                });
            }

            toast.success('Consolidated assessment completed successfully!');

            // Navigate to dashboard after a delay
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);

        } catch (error) {
            console.error('Consolidated assessment error:', error);
            toast.error(error.message || 'Consolidated assessment failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (checkingPayment) {
        return (
            <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-[#666666]">Loading consolidation data...</p>
                </div>
            </div>
        );
    }

    const enoughExperiences = workExperiences.length >= 2;

    return (
        <div className="min-h-screen bg-[#F9F9F6]">
            <SEO title="Assessment | MyProfessionalPath"
                 description="myprofessionalpath is an online professional development system. The system is aligned with
                 the Engineering Council of South Africa for candidate engineers, technologists and technicians.  Progress
                 can be measured and tracked against the competence statements for the chosen registration category.
                 It can also be used for individual company CPD reporting.. The system is mobile and tablet compatible so
                 records can be accessed or added to on-the-go."
                 canonicalURL="https://myprofessionalpath.web.app" />
            <nav className="sticky-nav bg-white border-b border-[#DCDCDA]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn" className="flex items-center gap-2">
                            <ArrowLeft size={18} /> Back to Dashboard
                        </Button>
                    </div>
                </div>
            </nav>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-3">
                        <Stack size={36} weight="duotone" className="text-[#FF5A00]" />
                        <h1 className="font-heading text-4xl font-semibold text-[#0A0A0A]">Consolidated Assessment</h1>
                    </div>
                    <p className="text-base text-[#666666]">
                        A holistic AI evaluation of your <span className="font-mono font-semibold text-[#0A0A0A]">{workExperiences.length}</span> work experiences against all 11 ECSA outcomes to determine your overall readiness.
                    </p>
                </div>

                {!enoughExperiences ? (
                    <Card className="swiss-card p-12 text-center" data-testid="not-enough-card">
                        <Stack size={48} weight="duotone" className="text-[#666666] mx-auto mb-4" />
                        <h3 className="font-heading text-xl font-medium text-[#0A0A0A] mb-2">More work experiences needed</h3>
                        <p className="text-sm text-[#666666] mb-6">You need at least 2 work experiences to run a consolidated assessment. You currently have {workExperiences.length}.</p>
                        <Button
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-to-dashboard-btn"
                            className="bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm"
                        >
                            Back to Dashboard
                        </Button>
                    </Card>
                ) : (
                    <>
                        <Card className="swiss-card p-6 mb-8">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#666666] mb-3">
                                Work Experiences Included ({workExperiences.length})
                            </p>
                            <div className="space-y-3">
                                {workExperiences.map((we, idx) => (
                                    <div key={we.id} className="flex items-start gap-3 p-3 border border-[#DCDCDA] rounded-sm" data-testid={`consolidated-we-${we.id}`}>
                                        <span className="bg-[#002FA7]/10 text-[#002FA7] px-2 py-1 rounded-sm text-xs font-medium flex-shrink-0">
                                            #{String(idx + 1).padStart(2, '0')}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-[#0A0A0A] text-sm">{we.title}</p>
                                            <p className="text-xs text-[#666666] mt-1 line-clamp-2">{we.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        {!hasPaid ? (
                            <Card className="swiss-card p-8" data-testid="consolidated-payment-card">
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="w-12 h-12 bg-[#FF5A00]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                                        <CreditCard size={24} weight="duotone" className="text-[#FF5A00]" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-heading text-xl font-medium text-[#0A0A0A] mb-2">Unlock Consolidated Assessment</h3>
                                        <p className="text-sm text-[#666666]">
                                            One payment unlocks a comprehensive analysis across all your work experiences to give you a single overall readiness verdict.
                                        </p>
                                    </div>
                                </div>

                                <div className="border border-[#DCDCDA] rounded-sm p-6 mb-6 bg-[#F9F9F6]">
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#666666] mb-2">CONSOLIDATED ASSESSMENT</p>
                                    <div className="flex items-baseline gap-2 mb-4">
                                        <span className="font-heading text-4xl font-semibold text-[#0A0A0A]">R{pricing.consolidated_price}</span>
                                        <span className="text-sm text-[#666666]">/ holistic assessment</span>
                                    </div>
                                    <ul className="space-y-2 text-sm text-[#666666]">
                                        <li className="flex items-start gap-2">
                                            <CheckCircle size={16} weight="fill" className="text-[#009944] flex-shrink-0 mt-0.5" />
                                            <span>Holistic evaluation across ALL your work experiences</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircle size={16} weight="fill" className="text-[#009944] flex-shrink-0 mt-0.5" />
                                            <span>One overall readiness verdict (Not Ready / Developing / Ready / Highly Ready)</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircle size={16} weight="fill" className="text-[#009944] flex-shrink-0 mt-0.5" />
                                            <span>Consolidated scores against all 11 ECSA outcomes</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircle size={16} weight="fill" className="text-[#009944] flex-shrink-0 mt-0.5" />
                                            <span>Gap analysis identifying outcomes that need more evidence</span>
                                        </li>
                                    </ul>
                                </div>

                                <div
                                    disabled={paying}
                                    data-testid="pay-consolidated-btn"
                                    className="w-full bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm py-6 text-base font-medium flex items-center justify-center gap-2"
                                >
                                    <CreditCard size={20} weight="duotone" />
                                    {paying ? 'Processing payment...' : <PaystackButton {...componentProps} />}
                                </div>
                                <p className="text-xs text-center text-[#666666] mt-3">
                                    Secure payment powered by Paystack. You&apos;ll be redirected to complete payment.
                                </p>
                            </Card>
                        ) : (
                            <Card className="swiss-card p-8">
                                <div className="flex items-center gap-3 mb-6 p-3 bg-[#009944]/10 rounded-sm" data-testid="consolidated-payment-confirmed">
                                    <CheckCircle size={20} weight="fill" className="text-[#009944]" />
                                    <p className="text-sm font-medium text-[#0A0A0A]">Payment confirmed. Ready to run your consolidated assessment.</p>
                                </div>

                                <div className="flex items-center gap-4 p-6 bg-[#F3F3F0] rounded-sm mb-6">
                                    <div className="w-12 h-12 bg-[#009944]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                                        <Sparkle size={24} weight="duotone" className="text-[#009944]" />
                                    </div>
                                    <div>
                                        <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-1">Google Gemini 3.1 Pro</h3>
                                        <p className="text-sm text-[#666666]">Advanced AI model for comprehensive consolidated analysis</p>
                                    </div>
                                </div>

                                <div className="p-4 bg-[#F3F3F0] rounded-sm mb-6">
                                    <p className="text-sm text-[#666666]">
                                        <strong className="text-[#0A0A0A]">Note:</strong> This consolidated assessment will analyse all {workExperiences.length} of your work experiences as a single career profile. It typically takes 45-90 seconds.
                                    </p>
                                </div>

                                <Button
                                    onClick={handleAssess}
                                    disabled={loading}
                                    data-testid="start-consolidated-btn"
                                    className="w-full bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm py-6 text-base font-medium disabled:opacity-50"
                                >
                                    {loading ? (
                                        <span className="flex items-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Analysing all experiences... Please wait
                                        </span>
                                    ) : (
                                        'Run Consolidated Assessment'
                                    )}
                                </Button>
                            </Card>
                        )}
                    </>
                )}
            </main>
        </div>
    );
};

export default ConsolidatedAssessmentPage;